import React from 'react';
import { Wifi, WifiOff, Globe, Clock, Headphones } from 'lucide-react';
import { ProcessingStage } from '../contexts/WebSocketContext';
import { AppError } from '../hooks/useAudioRecording';

export type CallState = 'idle' | 'recording' | 'processing' | 'playing' | 'error';

interface StatusDisplayProps {
  callState: CallState;
  processingStage: ProcessingStage | null;
  isConnected: boolean;
  error: AppError | null;
  isSessionActive: boolean;
  timer: number;
  formatTimer: (seconds: number) => string;
  isListening?: boolean;
}

export function StatusDisplay({
  callState,
  processingStage,
  isConnected,
  error,
  isSessionActive,
  timer,
  formatTimer,
  isListening
}: StatusDisplayProps) {
  // Helper function to get status text based on state
  const getStatusText = (): string => {
    if (isSessionActive) {
      if (callState === 'recording') return 'I hear you speaking...';
      if (callState === 'processing') return 'Processing your request...';
      if (callState === 'playing') return 'Speaking...';
      if (isListening) return 'Listening for your voice...';
      return 'Ready - just start speaking';
    }
    
    switch (callState) {
      case 'idle': return 'How can I help you today?';
      case 'recording': return 'I\'m listening...';
      case 'processing': return 'Processing your request...';
      case 'playing': return 'Speaking...';
      case 'error': return 'Service temporarily unavailable';
    }
  };

  // Determine avatar class
  const avatarClass = processingStage === 'generating' 
    ? 'avatar-processing'
    : `avatar-${callState}`;
  
  // Determine if showing processing info
  const showProcessing = (callState === 'processing' || processingStage === 'generating') && processingStage;
  
  // Get processing text
  const getProcessingText = (stage: ProcessingStage): string => {
    const stages = {
      transcribing: 'Converting speech...',
      processing: 'Generating the answer...',
      generating: 'Generating voice...'
    };
    return stages[stage] || '';
  };

  return (
    <div className="text-center space-y-6">
      {/* Header (when not in session) */}
      {!isSessionActive && (
        <div className="space-y-2">
          <div className="flex items-center justify-center space-x-2 text-qatar-gold">
            <Globe className="w-5 h-5" />
            <span className="text-lg font-semibold">Qatar Airways</span>
          </div>
          <p className="text-sm text-subtext">Voice Assistant Service</p>
        </div>
      )}

      {/* Avatar */}
      <div className="relative inline-block">
        <div className={`avatar-container ${avatarClass} w-28 h-28 rounded-full flex items-center justify-center overflow-hidden`}>
          <img src="/sama.png" alt="Qatar Airways Assistant" className="w-full h-full object-cover" />
        </div>
        
        {/* Connection indicator */}
        <div className="absolute -top-1 -right-1">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 border-white shadow-lg ${
            isConnected ? 'bg-green-500' : 'bg-red-500'
          }`}>
            {isConnected ? <Wifi className="w-3 h-3 text-white" /> : <WifiOff className="w-3 h-3 text-white" />}
          </div>
        </div>
        
        {/* Timer (when in call) */}
        {isSessionActive && (
          <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2">
            <div className="bg-qatar-maroon text-white text-xs px-4 py-1 rounded-full font-medium flex items-center space-x-1">
              <Clock className="w-3 h-3" />
              <span>{formatTimer(timer)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Status Information */}
      <div className="space-y-4">
        <h2 className="text-xl font-medium text-primary">{getStatusText()}</h2>
        
        {/* Processing indicator */}
        {showProcessing && (
          <div className="text-sm text-qatar-gold flex items-center justify-center space-x-2">
            <div className="w-4 h-4 border-2 border-qatar-gold border-t-transparent rounded-full animate-spin"></div>
            <span>{getProcessingText(processingStage)}</span>
          </div>
        )}
        
        {/* Service capabilities (idle, not in session, no error) */}
        {callState === 'idle' && !isSessionActive && !error && (
          <div className="space-y-4 text-sm">
            <div className="bg-gradient-to-r from-qatar-maroon/10 to-qatar-sunset/10 rounded-lg p-4 border border-qatar-maroon/20">
              <h3 className="text-qatar-gold font-medium mb-2 flex items-center justify-center space-x-2">
                <Headphones className="w-4 h-4" />
                <span>I can help you with:</span>
              </h3>
              <div className="text-subtext space-y-1">
                <p>• Flight information & booking</p>
                <p>• Check-in assistance</p>
                <p>• Baggage & seat selection</p>
                <p>• Travel requirements</p>
                <p>• Privilege Club services</p>
              </div>
            </div>
            
            <div className="text-xs text-overlay">
              <p>Press the call button to start your conversation</p>
              <p className="text-qatar-gold mt-1">Available 24/7 in multiple languages</p>
            </div>
          </div>
        )}
        
        {/* In-call status (when active and idle) */}
        {isSessionActive && callState === 'idle' && (
          <div className="text-sm text-subtext">
            {isListening ? (
              <>
                <p className="flex items-center justify-center space-x-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                  <span>Voice detection active</span>
                </p>
                <p className="text-xs text-overlay mt-1">I'll automatically detect when you speak</p>
              </>
            ) : (
              <>
                <p className="flex items-center justify-center space-x-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  <span>Ready to listen</span>
                </p>
                <p className="text-xs text-overlay mt-1">Just start speaking naturally</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
} 